import { EditFilled, EyeOutlined, PlusOutlined } from '@ant-design/icons';
import { message, Tooltip } from 'antd';
import { isEmpty } from 'lodash';
import { Dispatch, MouseEvent, SetStateAction, useEffect } from 'react';

import {
  Button,
  ExemptTag,
  InnerCollapse,
  Title,
} from '../../../../components/index';
import { usePatchFareMutation } from '../../../../services/fareApi';
import {
  AllTableItemsType,
  AllTableType,
  FareStatusType,
  FareType,
  TableType,
} from '../../../../typesFinancial';
import { tableTypeList } from '../../../../utils/lists';
import { formatNumberToLocale } from '../../../../utils/utils';
import { ChargeStatusTag } from '../ChargeStatusTag';
import {
  getFareExemptionTooltip,
  getTableExemptionTooltip,
} from '../exemption';
import {
  isMissingBillableRevenueCode,
  MissingRevenueCodeAlert,
} from '../FareRevenueCode';
import { FareTransmissionAlert } from '../FareTransmissionAlert';
import { FareTransmissionDetail } from '../FareTransmissionDetail';
import { FareItemsTable } from './fareItemsTable/FareItemsTable';

const { Panel: InnerPanel } = InnerCollapse;
const tablesList: TableType[] = [
  'table_one',
  'table_two',
  'table_three',
  'table_four',
  'table_five',
  'table_six',
  'table_seven',
  'table_eight',
  'table_nine',
];

type FaresTablesDataProps = {
  setSelectedItemTable: Dispatch<SetStateAction<AllTableItemsType>>;
  setItemTableViewDrawerVisible: Dispatch<SetStateAction<boolean>>;
  fare: FareType;
  onClickEdit: (
    e: MouseEvent<HTMLElement>,
    fare: FareType,
    isAddNewTableProp?: boolean,
    indexTable?: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9,
    editTableProp?: AllTableType
  ) => void;
  setSelectedTable: (value: React.SetStateAction<number>) => void;
  setIsSelectedTableExempt: (value: boolean) => void;
  title?: string;
  openStatementDrawer: (fare: FareType) => void;
  chargeId?: number;
  openATorchiveFireDrawer: (fare: FareType) => void;
  // A tarifa principal exibe status e desfecho da transmissão no header do painel,
  // que é do FaresData. A complementar é renderizada dentro desse painel e não tem
  // header próprio, então quem mostra os dois por ela é este componente.
  showStatus?: boolean;
};

// Editar o lote enquanto ele está a caminho do RM produziria divergência entre
// o que foi enviado e o que a tela mostra, então PROCESSING trava a edição
// junto dos status que já a travavam.
function isFareLockedForEditing(status?: FareStatusType) {
  return (
    status === 'BILLED' ||
    status === 'REBILLED' ||
    status === 'ARCHIVED' ||
    status === 'PROCESSING'
  );
}

// O backend recusa o reenvio quando a tentativa anterior gravou movimentações no
// RM, e nada limpa esse registro, então a tarifa não volta a ser enviável: o
// estorno no RM serve para o ERP parar de cobrar o que ficou pendurado, e os
// itens são cobrados por uma tarifa nova. Bloquear aqui evita o clique que só
// pode falhar, e as chaves ficam no FareTransmissionAlert em vez de irem embora
// com o toast de erro.
function hasRecordsPendingReversal(fare: FareType) {
  return (
    fare.status === 'SEND_ERROR' &&
    (fare.rm_accepted_record_keys?.length || 0) > 0
  );
}

// Status de onde o backend aceita o envio ao RM. Decide o "Gerar demonstrativo"
// daqui e as ações de envio do demonstrativo, que também abre por "Ver
// demonstrativo" numa tarifa que já foi enviada.
export function canSendFare(fare: FareType) {
  const status = fare.status || 'BILLING';

  return (
    (status === 'BILLING' ||
      status === 'REVERSED' ||
      status === 'SEND_ERROR') &&
    !hasRecordsPendingReversal(fare)
  );
}

function statementTooltip(fare: FareType) {
  return hasRecordsPendingReversal(fare)
    ? 'Movimentações desta tarifa já foram gravadas no TOTVS RM na tentativa anterior, então ela não pode mais ser enviada. Estorne essas movimentações dentro do RM e cobre os itens em uma tarifa nova.'
    : '';
}

export function FaresTablesData(props: FaresTablesDataProps) {
  const {
    setSelectedItemTable,
    setItemTableViewDrawerVisible,
    fare,
    onClickEdit,
    setSelectedTable,
    setIsSelectedTableExempt,
    title,
    openStatementDrawer,
    chargeId,
    openATorchiveFireDrawer,
    showStatus,
  } = props;

  const [
    patchFare,
    { isSuccess: isSuccessPatchFare, isLoading: isLoadingPatchFare },
  ] = usePatchFareMutation();

  useEffect(() => {
    if (isSuccessPatchFare)
      message.success('Demonstrativo estornado com sucesso');
  }, [isSuccessPatchFare]);

  function handleViewItem(
    item: AllTableItemsType,
    tableIndex?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9
  ) {
    setItemTableViewDrawerVisible(true);
    setSelectedItemTable(item);
    setIsSelectedTableExempt(
      !!tableIndex && !!fare[tablesList[tableIndex - 1]]?.exemption
    );
    if (tableIndex) setSelectedTable(tableIndex);
  }

  function getHeaderFareTable(typeTable: number, item: AllTableType) {
    const totalValue = item.discount
      ? item.total_value - (item.discount.total_value || 0)
      : item.total_value;
    return (
      <div className="header-fare-table">
        <strong style={{ fontWeight: '700' }}>Tabela {typeTable}</strong>
        {item.exemption ? (
          <ExemptTag tooltip={getTableExemptionTooltip(item.exemption)} />
        ) : (
          <span style={{ fontWeight: '600' }}>
            ({formatNumberToLocale(totalValue, true)})
          </span>
        )}
        <span style={{ fontWeight: '400' }}>
          {tableTypeList.find((item) => item.index === typeTable)?.label}
        </span>
      </div>
    );
  }

  function getExtraHeaderFareTable(
    typeTable: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9,
    fare: FareType,
    item: AllTableType
  ) {
    return (
      <Button
        className="extra-fare-table-edit"
        icon={<EditFilled />}
        htmlType="button"
        type="text"
        onClick={(e) => onClickEdit(e, fare, false, typeTable, item)}
        disabled={isFareLockedForEditing(fare.status)}
      >
        Editar
      </Button>
    );
  }

  function addDiscountToItems(table: AllTableType) {
    if (table && !table.exemption && !isEmpty(table.discount)) {
      const discount: AllTableItemsType = {
        id: 0,
        name: 'Descontos',
        value: table?.discount?.total_value || 0,
        discountItems: table.discount?.items,
      };
      return [...(table?.items || []), discount];
    }
    return table?.items || [];
  }

  function handleCancelStayment(fare: FareType) {
    if (fare.id && chargeId)
      patchFare({
        action: 'cancel_statement',
        id: fare.id,
        chargeId,
      });
  }

  function hasSomeTableItem(fare: FareType) {
    return (
      (fare.table_one?.items && fare.table_one.items.length > 0) ||
      (fare.table_two?.items && fare.table_two.items.length > 0) ||
      (fare.table_three?.items && fare.table_three.items.length > 0) ||
      (fare.table_five?.items && fare.table_five.items.length > 0) ||
      (fare.table_six?.items && fare.table_six.items.length > 0) ||
      (fare.table_seven?.items && fare.table_seven.items.length > 0) ||
      (fare.table_nine?.items && fare.table_nine.items.length > 0)
    );
  }
  return (
    <>
      <FareTransmissionAlert fare={fare} />
      {fare.statement ? (
        <Button
          className="button-demonstrative"
          onClick={() => handleCancelStayment(fare)}
          loading={isLoadingPatchFare}
        >
          Estornar demonstrativo
        </Button>
      ) : (
        <Tooltip title={statementTooltip(fare)}>
          <Button
            type="primary"
            className="button-demonstrative"
            onClick={() => openStatementDrawer(fare)}
            disabled={!hasSomeTableItem(fare) || !canSendFare(fare)}
          >
            Gerar demonstrativo
          </Button>
        </Tooltip>
      )}
      {(fare.status === 'BILLING' ||
        fare.status === 'REVERSED' ||
        fare.status === 'SEND_ERROR') && (
        <Button
          type="text"
          className="button-demonstrative"
          onClick={() => openATorchiveFireDrawer(fare)}
          style={{ opacity: 0.7 }}
        >
          Arquivar
        </Button>
      )}
      <div className="title-fares-by-table">
        <Title>
          {title || 'TARIFAS POR TABELA'} (
          {formatNumberToLocale(fare.total_value, true)})
        </Title>
        {showStatus && (
          <div>
            <ChargeStatusTag
              style={{ fontSize: '12px' }}
              status={fare.status || 'BILLING'}
            />
            {fare.is_exempt && (
              <ExemptTag tooltip={getFareExemptionTooltip(fare.status)} />
            )}
            <FareTransmissionDetail fare={fare} />
          </div>
        )}
        <div>
          {fare.statement && (
            <Button
              type="primary"
              icon={<EyeOutlined />}
              onClick={() => openStatementDrawer(fare)}
            >
              Ver demonstrativo
            </Button>
          )}
          <Button
            icon={<PlusOutlined />}
            onClick={(e) => onClickEdit(e, fare, true)}
            disabled={isFareLockedForEditing(fare.status)}
          >
            Adicionar tabela
          </Button>
        </div>
      </div>
      {showStatus && isMissingBillableRevenueCode(fare) && (
        <div className="fare-revenue-code-row">
          <MissingRevenueCodeAlert fare={fare} />
        </div>
      )}
      <InnerCollapse className="white-header" expandIconPosition="end">
        {tablesList.map((table: TableType, index) => {
          const tableIndex = index + 1;
          return (
            fare[table] &&
            tableIndex !== 0 &&
            (tableIndex === 1 ||
              tableIndex === 2 ||
              tableIndex === 3 ||
              tableIndex === 5 ||
              tableIndex === 6 ||
              tableIndex === 7 ||
              tableIndex === 8 ||
              tableIndex === 9) && (
              <InnerPanel
                key={`fareTableItems${tableIndex}`}
                header={getHeaderFareTable(
                  tableIndex,
                  fare[table] || ({} as AllTableType)
                )}
                extra={getExtraHeaderFareTable(
                  tableIndex,
                  fare,
                  fare[table] || ({} as AllTableType)
                )}
                className="panel-fare-items-table"
              >
                <FareItemsTable
                  fareItems={
                    addDiscountToItems(fare[table] || ({} as AllTableType)) ||
                    []
                  }
                  onEdit={handleViewItem}
                  tableIndex={tableIndex}
                  isExempt={!!fare[table]?.exemption}
                />
              </InnerPanel>
            )
          );
        })}
      </InnerCollapse>
    </>
  );
}
